<i18n locale="en">
{
  "filter-label": "Filter values",
  "placeholders": {
    "fallback": "Select a value",
    "filter": "Filter values..."
  },
  "labels": {
    "empty": "No options found"
  }
}
</i18n>

<template>
  <ComboboxRoot
    v-model="selectedItem"
    :search-term="searchTerm"
    class="own-dropdown"
    :filter-function="filterFunction"
    :display-value="displayValue"
    :disabled="props.disabled"
  >
    <ComboboxAnchor as-child>
      <ComboboxTrigger as-child :disabled="props.disabled">
        <OwnCard
          class="w-full own-dropdown__trigger"
          :class="[
            props.error && 'own-dropdown__trigger--error',
            props.disabled && 'own-dropdown__trigger--disabled',
          ]"
          :aria-invalid="props.error"
          :border-color="props.error ? 'danger' : undefined"
          :aria-disabled="props.disabled"
          el="button"
          control
        >
          <div class="flex-row gap-2 align-center justify-between w-full">
            <div
              v-if="!filterConfigured"
              class="flex-row own-dropdown__trigger-text-container gap-2 align-center"
            >
              <slot name="trigger-left" :item="selectedValue">
                <Component
                  :is="selectedValue?.icon"
                  v-if="selectedValue?.icon"
                  class="flex-shrink-0"
                  size="16"
                />
              </slot>

              <OwnType
                class="text--truncate"
                :text="triggerLabel"
                el="span"
                variant="title-regular"
                :color="placeholderColor"
              />
            </div>

            <ComboboxInput
              v-else
              v-model="searchTerm"
              class="own-dropdown__input"
              :placeholder="
                selectedValue?.label ||
                props.placeholder ||
                t('placeholders.filter')
              "
              :aria-label="t('filter-label')"
            />

            <PhCaretDown
              class="text-color-primary flex-shrink-0"
              size="16"
              weight="bold"
            />
          </div>
        </OwnCard>
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxPortal>
      <ComboboxContent
        data-click-outside-ignore
        class="own-dropdown__content"
        :side-offset="8"
        position="popper"
      >
        <OwnCard class="own-dropdown__menu" control>
          <ComboboxViewport
            class="own-dropdown__viewport"
            :style="{ maxHeight: maxContentHeight }"
          >
            <ComboboxItem
              v-for="item in safeOptions"
              :key="item.value"
              :value="item"
              class="own-dropdown__item"
              :class="[item.color === 'danger' && 'own-dropdown__item--danger']"
            >
              <slot name="left" :item="item">
                <Component
                  :is="item.icon"
                  v-if="item.icon"
                  class="flex-shrink-0"
                  size="24"
                />
              </slot>

              <slot name="label" :item="item">
                <OwnType
                  class="flex-1 text--truncate"
                  el="span"
                  :text="item.label"
                  variant="title-regular"
                  color="inherit"
                  :title="item.label"
                />
              </slot>

              <slot name="right" :item="item" />
            </ComboboxItem>

            <!-- Show when no options exist at all (before any search) -->
            <div v-if="safeOptions.length === 0" class="own-dropdown__empty">
              <OwnType
                :text="props.emptyText ?? t('labels.empty')"
                variant="title-regular"
              />
            </div>

            <!-- Show when search returns no matches -->
            <ComboboxEmpty v-else class="own-dropdown__empty">
              <OwnType
                :text="props.emptyText ?? t('labels.empty')"
                variant="title-regular"
              />
            </ComboboxEmpty>
          </ComboboxViewport>

          <slot name="actions" :close="closeDropdown" />
        </OwnCard>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<script lang="ts" setup generic="T extends Record<string, string | undefined>">
import { PhCaretDown } from '@phosphor-icons/vue'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxTrigger,
  ComboboxViewport,
} from 'reka-ui'
import { computed, markRaw, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnCard } from '../OwnCard'
import { OwnType } from '../OwnType'

import { type OwnDropdownItem } from './types'

type OptionType = OwnDropdownItem & T

const props = withDefaults(
  defineProps<{
    accessibilityId?: string
    error?: boolean
    filterKeys?: (keyof T)[]
    selfFilter?: boolean
    options: OptionType[]
    placeholder?: string
    visibleOptions?: number
    disabled?: boolean
    emptyText?: string
  }>(),
  {
    accessibilityId: undefined,
    disabled: false,
    emptyText: undefined,
    error: false,
    filterKeys: () => [],
    placeholder: undefined,
    selfFilter: false,
    visibleOptions: 4,
  }
)

const { t } = useI18n()

const LINE_HEIGHT = 53
const PADDING_HEIGHT = 8

const modelVal = defineModel<string | number>()
const filterValue = defineModel<string | undefined>('filter', { default: '' })

// Internal state
const searchTerm = ref('')

// Mark icon components as raw to prevent Vue reactivity warnings
const safeOptions = computed(() => {
  return props.options.map((opt) =>
    opt.icon ? { ...opt, icon: markRaw(opt.icon) } : opt
  )
})

// Map between value (string | number) and full item for ComboboxRoot
const selectedItem = computed({
  get: (): OptionType | undefined => {
    return safeOptions.value.find((option) => option.value === modelVal.value)
  },
  set: (item: OptionType | undefined) => {
    modelVal.value = item?.value
  },
})

// Sync searchTerm with v-model:filter for selfFilter consumers
watch(searchTerm, (newTerm) => {
  filterValue.value = newTerm
})

watch(filterValue, (newFilter) => {
  if (newFilter !== searchTerm.value) {
    searchTerm.value = newFilter ?? ''
  }
})

const filterConfigured = computed(() => {
  return props.filterKeys.length > 0 || props.selfFilter
})

// Filter function for ComboboxRoot
const filterFunction = (options: OptionType[], term: string): OptionType[] => {
  // If selfFilter is enabled, parent handles filtering - return all options
  if (props.selfFilter) {
    return options
  }

  // If no filter configured or no search term, return all
  if (!filterConfigured.value || !term) {
    return options
  }

  // Filter using filterKeys
  return options.filter((option) => {
    return props.filterKeys.some((key) => {
      const target = option[key]
      if (target && term) {
        return target.toLowerCase().includes(term.toLowerCase())
      }
      return false
    })
  })
}

// Display value for the input when an item is selected
const displayValue = (item: OptionType | undefined): string => {
  return item?.label ?? ''
}

const selectedValue = computed(() => {
  return selectedItem.value
})

const triggerLabel = computed(() => {
  return (
    selectedValue.value?.label ||
    props.placeholder ||
    t('placeholders.fallback')
  )
})

const placeholderColor = computed(() => {
  if (props.error) return 'danger'
  return selectedValue.value ? 'primary' : 'secondary'
})

const maxContentHeight = computed(() => {
  return `${LINE_HEIGHT * props.visibleOptions + PADDING_HEIGHT}px`
})

const closeDropdown = () => {
  searchTerm.value = ''
}
</script>

<style lang="scss">
@use '@/styles/globals';

.own-dropdown {
  width: 100%;
  position: relative;

  &__trigger {
    padding: 12px 16px;
    cursor: pointer;
    // Reset button defaults
    font: inherit;
    text-align: left;
    line-height: inherit;
    color: inherit;

    transition: globals.transitionOne(background-color),
      globals.transitionOne(border);

    &:hover,
    &:active,
    &:focus-within,
    &[data-state='open'] {
      background-color: var(--background-highlight);
      border-color: rgba(var(--raw-misc-brand), 0.05);
    }

    &--error {
      border-color: var(--status-danger);

      &:hover,
      &:active,
      &:focus-within {
        border-color: var(--status-danger);
      }
    }

    &--disabled,
    &--disabled:hover,
    &--disabled:active {
      cursor: not-allowed;
      background-color: var(--background-secondary);
      border-color: var(--divider);
    }
  }

  &__trigger-text-container {
    max-width: calc(100% - 24px);
  }

  &__input {
    flex: 1 1 0;
    min-width: 0;
    background: transparent;
    border: none;
    outline: none;
    font-family: inherit;
    font-weight: 600;
    line-height: 1.3125rem; // Match OwnType title-regular (21px)
    color: var(--text-color-primary);

    &::placeholder {
      color: var(--text-color-secondary);
    }
  }

  &__content {
    width: var(--reka-combobox-trigger-width);
    z-index: 9999;
  }

  &__menu {
    padding: 4px;
    border-radius: 12px;
  }

  &__viewport {
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    border-radius: 4px;
    cursor: pointer;
    color: var(--text-color-secondary);

    transition: background-color 100ms ease-in;

    &:hover,
    &[data-highlighted] {
      background-color: var(--background-highlight);
    }

    &[data-state='checked'] {
      background-color: var(--background-highlight);
      color: var(--text-color-primary);
    }

    &--danger {
      color: var(--status-danger);
    }
  }

  &__empty {
    padding: 8px;
    text-align: center;
  }
}
</style>
